import { MathUtil } from "@base/math/MathUtil";
import { lec, ltc } from "@base/util/Comma";
import { StringUtil } from "@base/util/StringUtil";
import { PartListGroup } from "@biz/Definitions";
import { IRuleArticle } from "@biz/definitions/ArticleDefinitions";
import { ShipmentType } from "@biz/definitions/ProductDefinitions";
import { Pvi } from "@biz/definitions/ProductVersion";
import { CalcProductLogic } from "@biz/items/CalcProductLogic";
import { IRaffData, RaffType } from "@biz/items/raff/RaffDefinitions";
import { RaffLogic } from "@biz/items/raff/RaffLogic";
import { AdditionalArticleInfo } from "@biz/logic/AdditionalArticleInfo";
import { Measure } from "@biz/logic/Measure";
import { IShoppingArticleOptions } from "@biz/models/ShoppingListDefinitions";
import { ICalcContext, ProjectCheckErrorType } from "@biz/projectCalculation/ProjectDefinitions";

export type AddRaffItemFunction = (articleId: string | undefined, options: Partial<IShoppingArticleOptions>) => void;

interface IArticleWithLength {
  articleNumber: string;
  length: number;
  mul: number | undefined;
}
interface IArticlesForSplit {
  "1": string;
  "2": string;
  "var": string;
}

const guideRaffMArticlesForSplit: IArticlesForSplit = {
  "1": "00E13001537",
  "2": "00E13001538",
  "var":  "00E13001129"
};

const profileArticlesForSplit: IArticlesForSplit = {
  "1": "00E13002027",
  "2": "00E13002028",
  "var":"00E13001778"
};

const aluTubeForSplit: IArticlesForSplit = {
  "1": "00E13002129",
  "2": "00E13002130",
  "var":"00E13002131"
};

export class RaffItemCalculator {
  static getStandardArticle(length: number, splitArticles: IArticlesForSplit): string | undefined {
    const l = "" + MathUtil.round(length, 3);
    return splitArticles[l as keyof IArticlesForSplit];
  }

  static calcGuideSplitArticles(raff: IRaffData): IArticleWithLength[] {
    const articles: ReturnType<typeof RaffItemCalculator.calcGuideSplitArticles> = [];
    if (raff.raffType === RaffType.C) {
      const length = RaffLogic.getRaffLength(raff);
      const guideLength = length - 0.15;
      if (lec(guideLength, 6.0, 3)) {
        articles.push({ articleNumber: "00E13001308", length: guideLength, mul: guideLength });
      } else {
        articles.push({ articleNumber: "00E13001308", length: 5.5, mul: 5.5 });
        articles.push({ articleNumber: "00E13001308", length: guideLength - 5.5, mul: guideLength - 5.5 });
      }
      return articles
    } else if (raff.raffType === RaffType.M_Guide) {
      const length = RaffLogic.getRaffLength(raff);
      const shipment = RaffLogic.getShipment(raff);

      return RaffItemCalculator.calcSplitArticles(length, shipment, guideRaffMArticlesForSplit, 0);
    }
    throw new Error("calcGuideSplitArticles not implemented for raffType " + raff.raffType);
  }

  static calcSplitArticles(width: number, shipment: ShipmentType, articlesForSplit: IArticlesForSplit, connectorWidth: number): IArticleWithLength[] {
    if (shipment === "post") {
      return RaffItemCalculator.#calcPostSplits(width, articlesForSplit, connectorWidth);
    } else if (shipment === "spedition") {
      return RaffItemCalculator.#calcSpeditionSplits(width, RaffLogic.getMaxShipmentLength(shipment), articlesForSplit, connectorWidth);
    }
    return [];
  }

  static #calcPostSplits(width: number, articlesForSplit: IArticlesForSplit, connectorWidth: number): IArticleWithLength[] {
    const articles: IArticleWithLength[] = [];
    let rest = MathUtil.round(width, 3);
    while (rest > 0.001) { // https://plaspack.atlassian.net/browse/SSK-30
      const standardArticle = RaffItemCalculator.getStandardArticle(rest, articlesForSplit);
      if (standardArticle) {
        articles.push({ articleNumber: standardArticle, length: rest, mul: undefined })
        rest = 0;
      } else if (ltc(rest, 2, 3)) {
        articles.push({ articleNumber: articlesForSplit.var, length: rest, mul: rest });
        rest = 0;
      } else if (ltc(rest, 3, 3)) {
        articles.push({ articleNumber: articlesForSplit["1"], length: 1, mul: undefined })
        rest -= 1 - connectorWidth;
      } else {
        articles.push({ articleNumber: articlesForSplit["2"], length: 2, mul: undefined })
        rest -= 2 - connectorWidth;
      }
    }
    return articles;
  }

  static #calcSpeditionSplits(width: number, maxLength: number, articlesForSplit: IArticlesForSplit, connectorWidth: number): IArticleWithLength[] {
    const articles: IArticleWithLength[] = [];

      const pieces = Math.ceil((width - connectorWidth) / maxLength);
      const pieceLength = (width - (pieces-1)*connectorWidth) / pieces;
      const standardArticle = RaffItemCalculator.getStandardArticle(pieceLength, articlesForSplit);
      for (let i = 0; i < pieces; i++) {
        articles.push({ articleNumber: standardArticle || articlesForSplit.var, length: pieceLength, mul: standardArticle ? undefined : pieceLength });
      }
      return articles;
  }

  static calcProfileConnectorCount(splitArticlesCount: number, fieldCount: number): number {
    return (splitArticlesCount - 1) * (fieldCount + 1);
  }

  static calcRaffC(
    slc: ICalcContext,
    raff: IRaffData,
    addItem: AddRaffItemFunction,
  ) {
    const length = RaffLogic.getRaffLength(raff);
    const width = RaffLogic.getRaffWidth(raff);
    const guideInfo = RaffLogic.calcGuideInfo(raff)
    const fieldInfo = RaffLogic.calcFieldInfo(raff);
    const countGuides = guideInfo.guideCount;
    const countFields = fieldInfo.fieldCount;
    const hasPreMontage = !!RaffLogic.getPreMontage(raff);

    const preMontageGroup = "Vormontage";

    const isLong = RaffLogic.isLong(length);
    {
      const group = "Profilzuschnitt Raff-C";
      addItem("00E13001123", { partList: { group }, mul: width - 0.01, count: 1, mulUnit: "m", additionalArticleInfo: { length: (article) => AdditionalArticleInfo.lengthToMM(slc, article.mul ?? 1) } }) // "Trageprofil für Motoreinheit"
      addItem("00E13001124", {
        partList: { group}, mul: width - 0.33, mulUnit: "m",
        additionalArticleInfo: { length: (article) => AdditionalArticleInfo.lengthInM(slc, article.mul ?? 1) }
      }) // "Antriebswelle"

      RaffItemCalculator.addGuides(slc, addItem, group, raff);

      RaffItemCalculator.addSailMount(slc, raff, addItem, hasPreMontage ? preMontageGroup : group);
    }
    {
      const group = "Führungsschiene Raff-C mit Antrieb";
      addItem("00E13001130", { partList: { group}, count: countGuides }) // "Umlenkblock Vorne inkl. Zahnrad"
      addItem("00E13001131", { partList: { group}, count: countGuides }) // "Abdeckkappe Vorne"
      addItem("00E13001133", { partList: { group}, count: countGuides }) // "Blechschrauben DIN 968 Torx schwarz, verzinkt mit Bund 4x10"
      addItem("00E13001143", { partList: { group }, count: 1, mul: length * 2 * countGuides + 0.3, mulUnit: "m", additionalArticleInfo: { length: (article) => AdditionalArticleInfo.lengthToMM(slc, article.mul ?? 1) } }) // "Zahnriemen f. Raff"
      const countPackages = isLong ? countGuides * 2 : countGuides;
      addItem("00E13001144", { partList: { group}, count: countPackages }) // "Klemmstück Zahnriemenverbindung"

      addItem("00E13001137", { partList: { group}, count: countGuides - 1 }) // "Tragprofilhalter Gewinkelt A2 - Links"
      addItem("00E13001306", { partList: { group}, count: 1 }) // "Tragprofilhalter Gewinkelt A2 - Rechts"
      addItem("00E13001138", { partList: { group}, count: countGuides }) // "Tragprofilhalter Gerade A2"
      addItem("00E13001139", { partList: { group}, count: countGuides * 2 }) // "Getriebe Deckel"
      addItem("00E13001122", { partList: { group}, count: countGuides }) // "Wellenkupplung eckige Nut (20059990340)"
      addItem("00E13001140", { partList: { group}, count: countGuides }) // "Wellenaufnahme mit Zahnrad"
      addItem("00E13001386", { partList: { group}, count: countGuides * 4 }) // "Spann - Schrauben Panhat 4x35 A2 VLG TX20"
      addItem("00E13001248", { partList: { group}, count: countGuides }) // "Kederkanalbolzen für CS 14mm M6"
      addItem("00E13000573", { partList: { group}, count: countGuides }) // "Inbus Linsenkopfschrauben M6x16 A2 DIN7380-2"
      addItem("00E13001315", { partList: { group}, count: countGuides }) // "Karosseriescheibe - WS9240 A2 6,4x25x1,5 für M6"
    }
    {
      const group = "Motoreinheit Raff-C";
      addItem("00E13001125", { partList: { group}, count: 1 }) // Motor GJ5606 für Raffbeschattung inkl. Kabel und Hirschmannstecker
      // OrderFormManager.addRaffItem(stockList, inputArgs, group, "00E13000696", "Hischmannstecker (Buchse)", 1); // hinzufügen Mail 20.3.2020, doch nicht - weil optional in App...
      addItem("00E13000315", { partList: { group}, count: 4 }) // "Inbus Linsenkopfschraube M5x10 SW 3mm Niro"
      addItem("00E13001175", { partList: { group}, count: 4 }) // name: "Scheibe M5 5,3x15x1,2 A2"
      addItem("00E13001427", { partList: { group}, count: 1 }) // name: "Motorschutzblech"
      addItem("00E13000577", { partList: { group}, count: 5 }) // name: "Bohrschraube selbstschn. Linsenkopf 7504N 4,2x13"
      addItem("00E13001127", { partList: { group}, count: 2 }) // name: "Distanzbuchse (20059990560)"
      addItem("00E13001128", { partList: { group}, count: 2 }) // name: "Befestigungswinkel f. Motoreinheit"
      addItem("00E13001316", { partList: { group}, count: 2 * countGuides + 2 }) // name: "Sechskantkopfschraube A2 M6x20",

      // addItem("00E13001726", { group, name: "Kederschnur 4,3mm", count: 1, mul: Math.ceil((width * (countFields + 1))), offerList: "N", hideForOffer: true }) // eingenäht https://plaspack.atlassian.net/browse/APP-761
      addItem("AZ000011", { partList: { group, list: "none" }, count: 1, mul: 197.2, articleGroup: "hidden" }) // name: "Fertigungskosten (1 Einheit = 1€)"
    }
    {
      const group = "Zubehör Raff-C";
      addItem("00E13001132", { partList: { group}, count: isLong ? 0 : countGuides }) // name: "Laufschlitten Angetrieben kurz"
      addItem("00E13001312", { partList: { group}, count: isLong ? countGuides : 0 }) // name: "Laufschlitten Angetrieben lang",
      addItem("00E13001570", { partList: { group}, count: countGuides * 2 }) // name: "Linsenblechschrauben 4,2x19 DIN 7981",
      addItem("00E13000820", { partList: { group}, count: 2 }) // name: "Befestigungsplatte Segel",

      addItem("00E13000543", { partList: { group}, count: 2 }) // name: "Beilagscheib f M4",
      addItem("00E13000800", { partList: { group}, count: 2 }) // name: "Inbus Linsenkopfschrauben",
      addItem("00E13001717", { partList: { group}, count: (countFields - 1) * countGuides }) // name: "Aufhängeelement f. Tuchprofil",

      addItem("00E13001109", { partList: { group}, count: 2 })
      addItem("00E13001715", { partList: { group}, count: countGuides * countFields - countGuides }) // Soliday Gleitrolle  mit Kugellager und Karabiner
    }
  }

  static calcMGuide(
    slc: ICalcContext,
    raff: IRaffData,
    addItem: AddRaffItemFunction,
  ) {
    const guideInfo = RaffLogic.calcGuideInfo(raff)
    const fieldInfo = RaffLogic.calcFieldInfo(raff);
    const countGuides = guideInfo.guideCount;
    const countFields = fieldInfo.fieldCount;
    const hasNotch = !!RaffLogic.getNotch(raff);
    const rollType = raff.rollType;
    const hasPreMontage = !!RaffLogic.getPreMontage(raff);

    const preMontageGroup = "Vormontage";
    {
      const group = "Profilzuschnitt Raff-M";

      RaffItemCalculator.addGuides(slc, addItem, group, raff);
      addItem(RaffLogic.getNotch(raff), { partList: { group }, count: countGuides }) // name: "Stopper mit Schraube für Führungsschiene-M",
      addItem("00E13001107", { partList: { group}, count: hasNotch ? 1 * countGuides : 3 * countGuides }) // name: "Stopper mit Schraube für Führungsschiene-M",
      addItem("00E13001109", { partList: { group}, count: 2 }) // Bohrschablone für Führungsschiene

      RaffItemCalculator.addSailMount(slc, raff, addItem, hasPreMontage ? preMontageGroup : group);
    }

    {
      const group = "Zubehör Raff-M";
      addItem("00E13001717", { partList: { group}, count: countGuides * (countFields + 1) + 1 }) // name: "Aufhängeelement f. Tuchprofil",


      // addItem("00E13001726", { group, name: "Kederschnur 4,3mm", count: 1, mul: Math.ceil((width * (countFields + 1))), offerList: "N", hideForOffer: true }) // eingenäht https://plaspack.atlassian.net/browse/APP-761
      addItem("AZ000011", { partList: { group, list: "none" }, count: 1, mul: 74.8, articleGroup: "hidden" }) // name: "Fertigungskosten (1 Einheit = 1€)",

      if (rollType === "metal")
        addItem("00E13001715", { partList: { group}, count: countGuides * (countFields + 1) }) // "Soliday-Gleitrolle Kunststoff Metall
      else
        addItem("00E13001300", { partList: { group}, count: countGuides * (countFields + 1) }) // "Soliday-Gleitrolle Kunststoff Metall
    }
  }

  private static addSailMount(slc: ICalcContext, raff: IRaffData, addItem: AddRaffItemFunction, group: PartListGroup) {
    const sailMount = RaffLogic.getSailMount(raff);
    if (sailMount === "hemstitch") {
      RaffItemCalculator.addHemstitch(slc, addItem, group, raff);
    } else if (sailMount === "profile") {
      RaffItemCalculator.addProfileTp3(slc, addItem, group, raff);
    }
  }

  static calcMRope(
    slc: ICalcContext,
    raff: IRaffData,
    addItem: AddRaffItemFunction,
  ) {
    const { version } = raff;
    const length = RaffLogic.getRaffLength(raff);
    const width = RaffLogic.getRaffWidth(raff);
    const guideInfo = RaffLogic.calcGuideInfo(raff)
    const fieldInfo = RaffLogic.calcFieldInfo(raff);
    const countGuides = guideInfo.guideCount;
    const countFields = fieldInfo.fieldCount;
    const hasPreMontage = !!RaffLogic.getPreMontage(raff);

    const preMontageGroup = "Vormontage";
    const seilrollen = countGuides * (countFields + 1);
    {
      const group = "Zubehör Raff-M";
      if (!Pvi.is(version, "2024")) { // old system
        addItem("00E13000255", { partList: { group }, count: 1, mul: Math.ceil(length * countGuides + width * 2.0), mulUnit: "m", additionalArticleInfo: { length: (article) => AdditionalArticleInfo.lengthToMM(slc, article.mul ?? 1) } }) // name: "Drahtseil aus Edelstahl; ø3mm",
        addItem("00E13000091", { partList: { group}, count: countGuides - 1 }) // name: "Wantenspanner Ring/Ring M6",
        addItem("00E13000664", { partList: { group}, count: (countGuides - 1) * 2 }) // name: "Seilklemmen Edelstahl f. Seil 3mm",
        addItem("00E13000026", { partList: { group}, count: countGuides - 2 }) // name: "Schäkel 50 mm mit Schnellverschluss",
        addItem("00E13000222", { partList: { group}, count: seilrollen }) // name: "Seilrollen beweglich",
        addItem("00E13000127", { partList: { group}, count: countGuides * 2 }) // name: "Seilrollen beweglich",
      } else {
        const tensioningSystem = RaffLogic.getRopeTensioningSystem(raff);
        if (tensioningSystem === "premium") {
          switch (RaffLogic.getRopeTensioningLengthType(raff)) {
            case "exact": {
              for (let i = 0; i < countGuides; i++) {
                const raffLength = RaffLogic.getRaffLength(raff);
                const ropeLength = RaffLogic.calcRopeLength(i, raffLength, raff.ropeOverrideLength);
                const productLen = Math.ceil(ropeLength);
                addItem("AMDS4GW" + StringUtil.pad("" + productLen, 2, "0"), {
                  features: { length: MathUtil.round(ropeLength, 3) },
                  partList: { group},
                  count: 1,
                  additionalArticleInfo: {
                    ropeLength: { header: slc.lc.t("ArticleInfo_Length"), text: Measure.toMM(ropeLength), filter: ["order", "posExtraText"] },
                    ropeCutLength: { header: slc.lc.t("ArticleInfo_RopeCutLength"), text: Measure.toMM(ropeLength + 0.12), filter: ["partList"] },
                    checkLength: { header: slc.lc.t("ArticleInfo_CheckLength"), text: Measure.toMM(ropeLength + 0.12 + 0.072 * 2 + 0.01), filter: ["partList"] } // https://plaspack.atlassian.net/browse/API-58
                  },
                })

                if (!raff.ropeOverrideLength?.[i])
                  CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Warning, "product", slc.lc.f("Rule_RaffM_RopeLengthNotSet", [i + 1, Measure.toMM(ropeLength)]));
                else if (ropeLength < raffLength) {
                  CalcProductLogic.addError(slc.errors, ProjectCheckErrorType.Error, "product", slc.lc.f("Rule_RaffM_RopeTooShort", [i + 1, Measure.toMM(ropeLength)]));
                }
              }
              break;
            }
            case "roundUp": {
              for (let i = 0; i < countGuides; i++) {
                const ropeLength = RaffLogic.calcRopeLength(i, RaffLogic.getRaffLength(raff), raff.ropeOverrideLength);
                const productLen = Math.ceil(ropeLength + 0.2);
                addItem("VPDS4GW" + StringUtil.pad("" + productLen, 2, "0"), { partList: { group}, count: 1 })
              }
              break;
            }
          }
        } else if (tensioningSystem === "basic") {
          const gewindeSpanner = [0, 0, 1, 2, 2, 3, 3][countGuides]; // https://plaspack.atlassian.net/browse/APP-1575
          const drahtseilKlemmen = [0, 0, 4, 4, 8, 12, 12][countGuides]; // https://plaspack.atlassian.net/browse/APP-1575
          addItem("00E13000126", { partList: { group }, count: gewindeSpanner }) // Gewindespanner
          addItem("00E13000664", { partList: { group }, count: drahtseilKlemmen }) // Drahtseilklemmen
          const ropeLength = Math.ceil(length * countGuides + width * 2.0 + 0.5);
          addItem("00E13000255", { partList: { group }, ...CalcProductLogic.calcDrahtSeilOptions(ropeLength, { count: 1 }) }) // https://plaspack.atlassian.net/browse/APP-1579
        }

        const ropeMount = RaffLogic.getRopeMountDetail(raff);
        if (ropeMount)
          addItem(ropeMount.articleNumber, { partList: { group}, count: countGuides * 2 / ropeMount.countPerSet })

        const suspension = RaffLogic.getRopeSuspension(raff);
        addItem(suspension, { partList: { group}, count: seilrollen })
      }
    }

    {
      const group = "Tuchprofile";
      RaffItemCalculator.addSailMount(slc, raff, addItem, hasPreMontage ? preMontageGroup : group);

      if (!Pvi.is(version, "2024")) {
        addItem("00E13001544", { partList: { group}, count: seilrollen }) // name: "Schäkel gedreht 4mm Niro",
        addItem("00E13001717", { partList: { group}, count: countGuides * (countFields + 1) + 1 }); // name: "Aufhängeelement f. Tuchprofil",
      }

      // addItem("00E13001726", { group, name: "Kederschnur 4,3mm", count: 1, mul: Math.ceil((width * (countFields + 1))), offerList: "N", hideForOffer: true }) // eingenäht https://plaspack.atlassian.net/browse/APP-761
      addItem("AZ000011", { partList: { group, list: "none" }, count: 1, mul: 74.8, articleGroup: "hidden" }) // name: "Fertigungskosten (1 Einheit = 1€)",
    }
  }

  static calc(
    slc: ICalcContext,
    raff: IRaffData,
    systemArticle: IRuleArticle | undefined,
  ): void {
    const { raffType } = raff;

    const addItem: AddRaffItemFunction = (articleNumber, _options): void => {
      if (!articleNumber)
        return;
      const options: Partial<IShoppingArticleOptions> = {
        parentArticle: systemArticle,
        offerList: "KH",
        ..._options
      }
      CalcProductLogic.addRuleArticle(slc, articleNumber, options)
    }

    if (raffType === RaffType.C) {
      RaffItemCalculator.calcRaffC(slc, raff, addItem);
    } else if (raffType === RaffType.M_Rope) {
      RaffItemCalculator.calcMRope(slc, raff, addItem);
    } else if (raffType === RaffType.M_Guide) {
      RaffItemCalculator.calcMGuide(slc, raff, addItem);
    }

    RaffItemCalculator.addRainCut(raff, addItem);
  }

  static addRainCut(raff: IRaffData, addItem: AddRaffItemFunction) {
    const rainCut = RaffLogic.getRainCut(raff);
    const length = RaffLogic.getRaffLength(raff);
    if (rainCut) { // https://plaspack.atlassian.net/browse/SSK-30
      const group = "Zubehör Raincut";
      addItem("00EK4916023", { partList: { group}, count: 1, mul: length + 0.1 }) // https://plaspack.atlassian.net/browse/APP-1451 // name: "Knopflochgurtband 18mm",
    } else if (RaffLogic.getAdditionalMaterialFactor(raff) > 0.00001) { // https://plaspack.atlassian.net/browse/SSK-30
      const group = "Zubehör Materialzugabe";
      addItem("00EK4916023", { partList: { group}, count: 2, mul: length }) // count: countGuides https://plaspack.atlassian.net/browse/SSK-30 // name: "Knopflochgurtband 18mm",
    }
  }

  static addProfileTp3(slc: ICalcContext, addItem: AddRaffItemFunction, group: PartListGroup, raff: IRaffData) {
    const profileWidth = RaffLogic.getRaffWidth(raff) - 0.006; // Abdeckkappen;
    const shipment = RaffLogic.getShipment(raff);
    const profileSplits = RaffItemCalculator.calcSplitArticles(profileWidth, shipment, profileArticlesForSplit, 0);

    const fieldInfo = RaffLogic.calcFieldInfo(raff);
    const countFields = fieldInfo.fieldCount;

    for (const split of profileSplits) {
      addItem(split.articleNumber, {
        partList: { group }, count: countFields + 1, mul: split.mul, mulUnit: split?.mul ? "m" : undefined, features: { length: split.length }, additionalArticleInfo: {
          length: (article) => AdditionalArticleInfo.lengthToMM(slc, article.features?.length ?? 1)
        }
      }) // "Tuchprofil TP3"
    }

    if (profileSplits.length >= 1) {
      addItem("00E13001785", { partList: { group }, count: RaffItemCalculator.calcProfileConnectorCount(profileSplits.length, countFields) }) // Aluminium-Verbinder für Tuchprofil TP3


      if (profileSplits.length >= 2) {
        CalcProductLogic.addRuleArticle(slc, "00E13001990", { partList: { group }, count: 1, articleType: "optional" }) // regular, optional article outside...
      }

      const abdeckkappen = (countFields + 1) * 2;
      addItem("00E13001777", { partList: { group }, count: abdeckkappen })
      addItem("00E13001783", { partList: { group }, count: abdeckkappen * 2 })
    }
  }

  static addHemstitch(slc: ICalcContext, addItem: AddRaffItemFunction, group: PartListGroup, raff: IRaffData) {
    const aluTubeWidth = RaffLogic.getRaffWidth(raff) - 0.06
    const fieldCount = RaffLogic.calcFieldCount(raff);
    const shipment = RaffLogic.getShipment(raff);
    const aluTubeSplits = RaffItemCalculator.calcSplitArticles(aluTubeWidth, shipment, aluTubeForSplit, 0);
    for (const split of aluTubeSplits) {
      addItem(split.articleNumber, {
        partList: { group }, count: fieldCount + 1, mul: split.mul, features: { length: split.length }, mulUnit: split.mul ? "m" : undefined, additionalArticleInfo: {
          length: (article) => AdditionalArticleInfo.lengthToMM(slc, article.features?.length ?? 1)
      }}) // "Alu-Rohr"
    }

    addItem("60AZUBS401", { partList: { group }, count: (fieldCount + 1) * (aluTubeSplits.length - 1) }) // Alustangen Verbinder
    addItem("00E13000874", { partList: { group }, count: (fieldCount + 1) * 2 }) // "Kunststoff Kappe für Rundrohr 12-13mm
  }

  static addGuides(slc: ICalcContext, addItem: AddRaffItemFunction, group: PartListGroup, raff: IRaffData) {
    const guideInfo = RaffLogic.calcGuideInfo(raff)
    const countGuides = guideInfo.guideCount;
    const hasNotch = RaffLogic.getNotch(raff);

    const guideSplits = RaffItemCalculator.calcGuideSplitArticles(raff);
    for (let i = 0; i < guideSplits.length; i++) {
      const split = guideSplits[i];
      const notchPossible = i === guideSplits.length - 1;
      const guideSplitHasNotch = hasNotch && notchPossible;

      addItem(split.articleNumber, {
        partList: { group},
        features: { notchSide: guideSplitHasNotch ? "R" : undefined, length: split.length },
        count: countGuides,
        mul: split.mul,
        mulUnit: split.mul ? "m" : undefined,
        stkUnit: "Stk",
        idCategory: "guide",
        additionalArticleInfo: {
          length: (article) => AdditionalArticleInfo.lengthToMM(slc, article.features?.length ?? 1),
          notch: !!guideSplitHasNotch && { header: slc.lc.t("ArticleInfo_Notch"), text: "✅" }
        }
      }) // name: "Führungsschiene Raff-C",
    }

    const connectorCount = (guideSplits.length - 1) * countGuides;
    if (raff.raffType === RaffType.C)
      addItem("00E13001454", { partList: { group }, count: connectorCount, idCategory: "Verbinder" }) // Verbinder
    else
      addItem("00E13001214", { partList: { group }, count: connectorCount, idCategory: "Verbinder" })

  }
}